import React, {useMemo} from 'react';

import {
  Box,
  Divider,
  Stack,
  SvgIcon,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {IconCreditCard} from '@zoom/icons-react';
import {Popover} from '@zoom/react-ui';
import {IconTeam} from '@zoom/sun-ui-icon';
import {addMinutes} from 'date-fns';
import {DateTime} from 'luxon';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {useResolvedTimeZone} from 'Utils/useTimeZone';

import {
  CardContent,
  CardHeader,
  CardDivider,
} from './Style';

import {ReactComponent as CalendarSimple} from 'assets/svg/calendar-icon-simple.svg';
import {ReactComponent as GlobeOutlined} from 'assets/svg/globe-outlined.svg';
import {TypographyBody2} from 'Components/Appointments/Style';
import Avatar from 'Components/Avatar';
import {toDateTime} from 'Components/BookingBlocksWidget/utils';
import MeetingType from 'Components/MeetingType/MeetingType.js';
import {IconContainer} from 'Components/MeetingType/Style';
import useRefundTips from 'hooks/useRefundTips';
import {
  extractLocationFromApptOrEvent,
  formatTimeIntervalLongDT,
  getApptType,
  getTimeFormatFromSetting,
  isCciCalendar,
} from 'Utils';
import {getPrice} from 'Utils/CardDisplay';
import {ADD_ON_TYPE} from 'Utils/consts';
import zoomPalette from 'zoomPalette';

const PaidSignSx = {
  display: 'inline-block',
  border: '1px solid #9ECEAD',
  height: '1.125rem',
  backgroundColor: '#F2FFF6',
  color: '#196830',
  borderRadius: '99px',
  padding: '0 5px',
  lineHeight: '1rem',
  fontSize: '10px',
  textTransform: 'uppercase',
  fontWeight: 600,
};

const RefundedSignSx = {
  ...PaidSignSx,
  backgroundColor: '#F2F8FF',
  borderColor: '#A8CCF8',
  color: '#2057B1',
};

const autoRefundTipsSx = (theme) => ({
  fontWeight: 400,
  fontSize: '0.75rem',
  lineHeight: '1rem',
  color: theme.palette.text.neutral,
  marginTop: '0.25rem',
});

const boxStyle = {
  margin: '8px 0px',
  minWidth: '260px',
};
const nameStyle = {
  fontSize: '.875rem',
  lineHeight: '1.5rem',
  fontWeight: '500',
};
const avatarStyle = {
  height: '1.5rem',
  width: '1.5rem',
  borderRadius: '8px',
  fontSize: '.75rem',
  lineHeight: '1.5rem',
};

/**
 * Event Info Component
 * @param {Object} props
 * @param {Object} props.eventInfo the formatted event info object, used to display information
 * @param {?Date} props.startDateTime
 * @param {?String} props.timeZone
 * @return {ReactElement}
 */
export default function EventInfo(props) {
  const {
    appointment,
    displayName,
    displayPreferredTimes,
    eventInfo,
    guests,
    hiddenFee,
    isBookerVoting,
    isPaidAppt,
    oldEventInfo,
    oldGuests,
    showAutoRefundPolicy,
    showAvatar,
    startDateTime,
    timeZone,
    votes,
  } = props;
  const {t} = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isCciAccount = isCciCalendar(appointment?.organizer?.email);
  const votesArr = votes ? Object.values(votes).sort((a, b) => a.start.valueOf() - b.start.valueOf()) : [];
  const timeFormat = useSelector((state) => state.hostSettingsState?.timeFormat?.value);

  const formatPreferredTimes = (date) => {
    const dateTime = DateTime.fromJSDate(date);
    return dateTime.toFormat('cccc, LLLL dd, ' +
      getTimeFormatFromSetting(timeFormat, true)
    );
  };

  const eventInterval = eventInfo ?
    formatTimeIntervalLongDT(
      toDateTime(eventInfo.originalEvent?.start.dateTime, timeZone),
      toDateTime(eventInfo.originalEvent?.end.dateTime, timeZone),
      timeFormat,
    ) :
    formatTimeIntervalLongDT(
      toDateTime(startDateTime, timeZone),
      toDateTime(addMinutes(startDateTime, appointment.duration), timeZone),
      timeFormat,
    );
  const {indexedOpts, timeZoneLabel: eventTimezone} = useResolvedTimeZone(timeZone);

  let oldEventInterval;
  let oldEventTimezone;
  if (oldEventInfo) {
    const oldTimeZone = oldEventInfo?.bookerInfo?.timeZone || oldEventInfo.originalEvent?.start.timeZone;
    oldEventInterval = formatTimeIntervalLongDT(
      toDateTime(oldEventInfo.originalEvent?.start.dateTime, oldTimeZone),
      toDateTime(oldEventInfo.originalEvent?.end.dateTime, oldTimeZone),
      timeFormat,
    );

    if (oldTimeZone !== timeZone) {
      oldEventTimezone = indexedOpts[oldTimeZone]?.label;
    }
  }

  const summary = eventInfo ?
    eventInfo?.summary || oldEventInfo?.summary || t('common.noTitle') :
    appointment?.summary || t('common.noTitle');

  const {addOnType, location, locationAdditionalInfo} = useMemo(() => {
    return extractLocationFromApptOrEvent(appointment, eventInfo);
  }, [appointment, eventInfo]);

  const price = getPrice(appointment, eventInfo || oldEventInfo);
  const showPrice = !hiddenFee && price;
  const includeTaxes = eventInfo?.attendees?.[0]?.tax || oldEventInfo?.attendees?.[0]?.tax;
  const autoRefundTips = useRefundTips(
    eventInfo?.attendees?.[0].autoRefundPolicy || oldEventInfo?.attendees?.[0].autoRefundPolicy
  );

  const isRefunded = eventInfo?.attendees?.[0]?.refunded || oldEventInfo?.attendees?.[0]?.refunded;

  return (
    <CardContent>
      <CardHeader>
        <Typography variant="h4" sx={{
          fontWeight: '700',
          fontSize: '1rem',
          lineHeight: '1.5rem',
          color: theme.palette.text.primary,
        }}>
          {summary}
        </Typography>
        {showAvatar &&
          <Avatar
            displayName={eventInfo ?
              eventInfo?.hostDisplayName || oldEventInfo?.hostDisplayName :
              displayName
            }
            boxStyle={boxStyle}
            nameStyle={nameStyle}
            avatarStyle={avatarStyle}
          />
        }
      </CardHeader>

      {!isSmallScreen && showAvatar && <CardDivider />}

      {
        isBookerVoting ?
        <TypographyBody2
          sx={{
            fontSize: '.875rem',
            marginTop: '.5rem',
          }}
        >
          <span style={{
            display: 'flex',
            maxWidth: '100%',
          }}>
            <SvgIcon fontSize="inherit" component={CalendarSimple} inheritViewBox />
            {
            displayPreferredTimes ?
            <div>
              {votesArr.map((votes) => {
                return <div>{formatPreferredTimes(votes.start)}</div>;
              })}
            </div> :
              <span>
                {votesArr.length === 1 ? t('polls.timePreferred', [votesArr.length]) :
                    t('polls.timesPreferred', [votesArr.length])}
              </span>
            }
          </span>
        </TypographyBody2> :
      <TypographyBody2
        sx={{
          fontSize: '.875rem',
          marginTop: '.5rem',
        }}
      >
        <IconContainer style={
          !oldEventInterval || (eventInterval === oldEventInterval) ?
          {} :
          {color: zoomPalette.green['60'], fontWeight: 'bolder'}
        }>
          <SvgIcon fontSize="inherit" component={CalendarSimple} inheritViewBox />
          {!!eventInterval && <span> {eventInterval} </span>}
        </IconContainer>
        {!!oldEventInterval && (eventInterval !== oldEventInterval) && <>
          <br />
          <span style={{
            marginLeft: '17px',
          }}>
            <del> {oldEventInterval} </del>
          </span>
        </>}
      </TypographyBody2>
      }

      <TypographyBody2
        sx={{
          fontSize: '.875rem',
          marginTop: '.5rem',
        }}
      >
        <IconContainer style={
          !oldEventTimezone || (eventTimezone === oldEventTimezone) ?
          {} :
          {color: zoomPalette.green['60'], fontWeight: 'bolder'}
        }>
          <SvgIcon fontSize="inherit" component={GlobeOutlined} inheritViewBox />
          {!!eventTimezone && <span> {eventTimezone} </span>}
        </IconContainer>
        {!!oldEventTimezone && (eventTimezone !== oldEventTimezone) && <>
          <br />
          <span style={{
            marginLeft: '17px',
          }}>
            <del> {oldEventTimezone} </del>
          </span>
        </>}
      </TypographyBody2>

      {((addOnType && addOnType !== ADD_ON_TYPE.OFFLINE) ||
        location) && !isCciAccount &&
        <TypographyBody2
          sx={{
            fontSize: '.875rem',
            marginTop: '.5rem',
            maxWidth: '100%',
          }}
        >
          <MeetingType
            apptType={getApptType(appointment)}
            addOnType={addOnType}
            location={location}
            locationAdditionalInfo={locationAdditionalInfo}
          />
        </TypographyBody2>
      }

      {(!!guests?.length || !!oldGuests?.length) && <TypographyBody2
        sx={{
          fontSize: '.875rem',
          marginTop: '.5rem',
        }}
      >
        <IconContainer style={
          !oldGuests || (JSON.stringify(guests) === JSON.stringify(oldGuests)) ?
          {} :
          {color: zoomPalette.green['60'], fontWeight: 'bolder'}
        }>
          <SvgIcon fontSize="inherit" component={IconTeam} inheritViewBox />
          <span>
            {`${eventInfo?.bookerInfo?.displayName} (${eventInfo?.bookerInfo?.email})`}
          </span>
        </IconContainer>
        {!!guests?.length && guests.map((guest, idx) => (
          <div style={
            !oldGuests || (JSON.stringify(guests) === JSON.stringify(oldGuests)) ?
            {
              marginTop: '8px',
              marginLeft: '19px',
            } :
            {
              marginTop: '8px',
              marginLeft: '19px',
              color: zoomPalette.green['60'],
              fontWeight: 'bolder',
            }
          }>
            {guest}
          </div>
        ))}
        {!!oldGuests?.length && (JSON.stringify(guests) !== JSON.stringify(oldGuests)) && <>
          <div style={{
            marginTop: '8px',
            marginLeft: '19px',
          }}>
            <del> {`${oldEventInfo?.bookerInfo?.displayName} (${oldEventInfo?.bookerInfo?.email})`} </del>
          </div>
          {oldGuests.map((guest) =>
            <div style={{
              marginTop: '8px',
              marginLeft: '19px',
            }}>
              <del> {guest} </del>
            </div>)}
        </>}
      </TypographyBody2>}

      {showPrice && <TypographyBody2 sx={{
        color: 'inherit', lineHeight: '1.125rem', marginTop: '.5rem',
      }}>
        <Stack direction="row" alignItems="flex-start" gap="0.5rem">
          <IconCreditCard style={{fontSize: '16px', lineHeight: '1.125rem', position: 'relative', top: '2px'}} />
          <Stack>
            <Stack direction="row" alignItems="flex-start" gap="0.5rem">
              <span style={isPaidAppt && !isRefunded ? {textDecoration: 'line-through'} : {}}>
                {price}
                &nbsp;
                {includeTaxes && <span style={{color: '#686F79'}}>{t('payment.includeTaxes')}</span>}
              </span>
              {isPaidAppt && !isRefunded && <Popover content={t('payment.paidDesc')} placement="top" trigger="hover">
                <Box sx={PaidSignSx}>{t('payment.paid')}</Box>
              </Popover>}
              {isRefunded && <Popover
                content={t('payment.refundedTip', {total: price})}
                placement="top"
                trigger="hover"
              >
                <Box sx={RefundedSignSx}>{t('payment.refunded')}</Box>
              </Popover>}
            </Stack>
            {showAutoRefundPolicy && <Typography sx={autoRefundTipsSx}>
              {autoRefundTips}
            </Typography>}
          </Stack>
        </Stack>
      </TypographyBody2>}

      <Divider sx={{margin: '16px 0px'}} />
    </CardContent>
  );
}
